import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip, Paper, Stack } from '@mui/material'
import { Container, Col, Row } from 'react-bootstrap'
import { Form } from 'antd'
import { FaEdit, FaCheck } from "react-icons/fa"
import { TbArrowsMinimize, TbArrowsMaximize } from "react-icons/tb"
import { MdDraw } from "react-icons/md"
import { useOutletContext } from 'react-router-dom'

import DataTable from 'Shared/Components/DataTable'
import PopupModal from 'Shared/Components/PopupModal'
import { TextInput, FormContainer } from 'Shared/Components/FormComponents'
import { showSuccess } from 'Shared/Components/NotifyToast'
import useFetchData from 'Shared/Hooks/useFetchData'
import { apiUrl } from 'Shared/utils'
import { IconButtonComponent } from 'Shared/Components/Icon'

import ZoneMap from './components/ZoneMap'
import ZoneMapEdit from './components/ZoneMapEdit'
import useWindowSize from 'Shared/Hooks/useWindowSize'

const ZoneTag = ({ zone }) => {
  const { t } = useTranslation()

  if (zone.in_tunnel) {
    return (<div>
      <Chip label={t("confinedSpace_inTunnel")} color="primary" variant="outlined" />
    </div>)
  }
  else if  (zone.standby_zone) {
    return (<div>
      <Chip label={t("confinedSpace_standbyZone")} color="primary" variant="outlined" />
    </div>)
  }
  else if  (zone.rest_zone) {
    return (<div>
      <Chip label={t("confinedSpace_restZone")} color="primary" variant="outlined" />
    </div>)
  }
}

const SmartwatchConfinedSpaceZones = () => {
  const { role, setPageLoading } = useOutletContext()

  const { t } = useTranslation()
  const { send } = useFetchData()
  const [locationForm] = Form.useForm()
  const [zoneForm] = Form.useForm()
  const mapRef = useRef()
  const windowSize = useWindowSize()

  const [locationFormOpen, setLocationFormOpen] = useState({ "location": null, "isOpen": false })
  const [zoneFormOpen, setZoneFormOpen] = useState({ "zone": null, "isOpen": false })
  const [zones, setZones] = useState()
  const [floorplans, setFloorplans] = useState()
  const [mapExt, setMapExt] = useState()
  const [modifying, setModifying] = useState()
  const [minimize, setMinimize] = useState(false)

  const tableRef = useRef()

  const fetchFloorPlan = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/floorplan_meta`,
      returnType: "json"
    })

    const floorplans = []

    const data = []

    if (body.length > 0) {
      for await (const i of body) {
        const blob = await send({
          method: "GET",
          url: `${apiUrl}/smartwatch/floorplan`,
          params: {
            "location": i["location"]
          },
          returnType: "blob"
        })

        const zone = await send({
          method: "GET",
          url: `${apiUrl}/smartwatch/zones`,
          params: {
            "location": i["location"]
          },
          returnType: "json"
        })

        if (blob != null) {
          floorplans.push({"blob": blob, "coor": i})
        }

        data.push({
          "id": i["location"],
          "location": i["location"],
          "locationName": i["location_name"],
          "address": i["address"],
          "content": zone.map((i) => ({
            "zone_id": i["zone_id"],
            "zone_name": i["zone_name"],
            "zone_description": i["zone_description"],
            "marker_lon": i["marker_lon"],
            "marker_lat": i["marker_lat"],
            "tag": <ZoneTag zone={i} />
          }))
        })
        setFloorplans(floorplans)
        setZones(data)
      }
    }
  }

  const onFinish = async (values) => {
    setPageLoading(true)

    let res

    if (zoneFormOpen["isOpen"]) {
      res = await send({
        method: "PUT",
        url: `${apiUrl}/smartwatch/zone/${zoneFormOpen["zone"]}`,
        contentType: "json",
        body: {
          "zone_name": values["zone_name"],
          "zone_description": values["zone_description"]
        }
      })
    }
    else if (locationFormOpen["isOpen"]) {
      res = await send({
        method: "PUT",
        url: `${apiUrl}/smartwatch/location/${locationFormOpen["location"]}`,
        contentType: "json",
        body: {
          "location_name": values["locationName"],
          "address": values["address"]
        }
      })
    }

    if (res != null) {
      toggleZoneForm()
      toggleLocationForm()

      showSuccess(t("general_success"))

      tableRef.current.setTableLoading(true)

      await fetchFloorPlan()

      tableRef.current.setTableLoading(false)
    }

    setPageLoading(false)
  }

  const toggleLocationForm = (row) => {
    if (row) {
      locationForm.setFieldsValue({
        "locationName": row["locationName"],
        "address": row["address"]
      })
    }
    else {
      locationForm.resetFields()
    }

    setLocationFormOpen({
      "location": row ? row["location"] : null,
      "isOpen": row != null
    })
  }

  const toggleZoneForm = (row2) => {
    if (row2) {
      zoneForm.setFieldsValue({
        "zone_name": row2["zone_name"],
        "zone_description": row2["zone_description"]
      })
    }
    else {
      zoneForm.resetFields()
    }

    setZoneFormOpen({
      "zone": row2 ? row2["zone_id"] : null,
      "isOpen": row2 != null
    })
  }

  const fetchMapExtent = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/map_extent`,
      returnType: "json"
    })

    setMapExt(body)
  }

  const toggleDraw = (location) => {
    setModifying(location ? location["location"] : null)
  }

  useEffect(() => {
    const init = async () => {
      setPageLoading(true)

      await Promise.all([
        fetchFloorPlan(),
        fetchMapExtent()
      ])

      setPageLoading(false)
    }

    init()    
  }, [])

  const submit = async () => {
    const zones = mapRef.current.getZones()
    const floorplans = mapRef.current.getFloorplan()

    setPageLoading(true)

    const res = await send({
      method: "PUT",
      url: `${apiUrl}/smartwatch/location_coor/${modifying}`,
      contentType: "json",
      body: {
        "zones": JSON.stringify(zones),
        "floorplans": JSON.stringify(floorplans.map((i) => ({
          "location": i["location"],
          "coor": [
            Math.min(i["coor"][0][0], i["coor"][1][0], i["coor"][2][0], i["coor"][3][0]),
            Math.min(i["coor"][0][1], i["coor"][1][1], i["coor"][2][1], i["coor"][3][1]),
            Math.max(i["coor"][0][0], i["coor"][1][0], i["coor"][2][0], i["coor"][3][0]),
            Math.max(i["coor"][0][1], i["coor"][1][1], i["coor"][2][1], i["coor"][3][1])
          ]
        })))
      }
    })

    if (res != null) {
      showSuccess(t("general_success"))

      setZones()
      setFloorplans()
      setMapExt()

      await Promise.all([
        fetchFloorPlan(),
        fetchMapExtent()
      ])
    }

    setModifying()
    setPageLoading(false)
  }

  return (<>
    <Container fluid className="mainContainer" style={{ backgroundColor: "white", borderRadius: "15px" }}>
      <Row style={{ height: minimize ? "100%" : "50%", marginBottom: "5px" }}>
        <Col xs={12} style={{ height: "100%", paddingLeft: 0, paddingRight: 0}}>
          {((modifying == null && zones && floorplans && mapExt) &&
            <ZoneMap zones={zones} floorplans={floorplans} mapExt={mapExt}/>
          )}           
        </Col>
      </Row>

      {minimize ? (
        <Row style={{ marginTop: "5px", display: minimize ? "block" : "none" }}>
          <div style={{ width: "100%", display: "flex", justifyContent: "end", paddingRight: "10px" }}>
            <IconButtonComponent  text={t("general_maximize")} onClick={() => setMinimize(false)}>
              <TbArrowsMaximize size={15}/>
            </IconButtonComponent>
          </div>
        </Row>
      ) : (
        <Row style={{ height: "50%" }}>
          <Col xs={12} style={{ height: "100%", overflow: "auto", paddingLeft: 0, paddingRight: 0}}>
            <Paper elevation={0} sx={{ height: "100%", display: "flex", flexFlow: "column" }}>
              <DataTable
                data={zones} count={null} ref={tableRef}
                columns={{
                  "locationName": { "label": t("smartwatch_location"), "sorting": false, "nowrap": true },
                  "address": { "label": t("smartwatch_address"), "sorting": false, "nowrap": false },
                }}
                subColumns={{
                  "zone_name": { "label": t("smartwatch_zoneName"), "sorting": false, "nowrap": true },
                  "zone_description": { "label": t("smartwatch_zoneDescription"), "sorting": false, "nowrap": false },
                  "tag": { "label": t("smartwatch_zoneTag"), "sorting": false, "nowrap": true }
                }}
                renderActions={(row) => { return [
                  ["pm", "admin"].includes(role) && {icon: <FaEdit size={20}/>, text: t("general_edit"), onClick: function(row) { toggleLocationForm(row) }},
                  ["pm", "admin"].includes(role) && {icon: <MdDraw size={20}/>, text: t("general_edit"), onClick: function(row) { toggleDraw(row) }},
                ]}}
                renderSubActions={(row) => { return [
                  ["pm", "admin"].includes(role) && {icon: <FaEdit size={20}/>, text: t("general_edit"), onClick: function(row1, row2) { toggleZoneForm(row2) }},
                ]}}
                appActions={() => { return [
                  {icon: <TbArrowsMinimize size={20}/>, text: t("general_minimize"), onClick: function() { setMinimize(true) }}
                ]}}
              />
            </Paper>
          </Col>
        </Row>
      )}
    </Container> 

    <PopupModal isOpen={zoneFormOpen["isOpen"]} close toggle={() => toggleZoneForm()} width="80%">
      <FormContainer form={zoneForm} onFinish={onFinish} initialValues={{}}>
        <TextInput field_name={t("smartwatch_zoneName")} field_id="zone_name"/>
        <TextInput field_name={t("smartwatch_zoneDescription")} field_id="zone_description"/>
      </FormContainer>
    </PopupModal>

    <PopupModal isOpen={locationFormOpen["isOpen"]} close toggle={() => toggleLocationForm()} width="80%">
      <FormContainer form={locationForm} onFinish={onFinish} initialValues={{}}>
        <TextInput field_name={t("smartwatch_location")} field_id="locationName"/>
        <TextInput field_name={t("smartwatch_address")} field_id="address"/>
      </FormContainer>
    </PopupModal>

    <PopupModal isOpen={modifying != null} close toggle={() => toggleDraw()} width={windowSize["width"]*0.8} height={windowSize["height"]*0.8}>
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ flex: 1, margin: "5px 5px 5px 5px" }}>
          <ZoneMapEdit ref={mapRef} modifying={modifying} zones={zones} floorplans={floorplans} mapExt={mapExt}/>
        </div>

        <div style={{ display: "flex", justifyContent: "end", padding: "5px 5px 5px 5px" }}>
          <Stack direction="row">
            <IconButtonComponent title={t("general_submit")} onClick={() => submit()}>
              <FaCheck size={20}/>
            </IconButtonComponent>
          </Stack>
        </div>
      </div>
    </PopupModal>
  </>)
}

export default SmartwatchConfinedSpaceZones
